import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Home,
  GitCompare,
  Bookmark,
  TrendingUp,
  Sparkles,
  BarChart3,
  Code2,
  Skull,
  Globe,
  Compass,
  Trophy,
  LineChart,
  Calendar,
  Award,
  Search,
  BookOpen,
  Tags,
  Menu,
  X,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import NavDropdown, { type NavDropdownItem } from './NavDropdown';

interface Props {
  darkMode: boolean;
  className?: string;
  /** Mobil menü dışarıdan kontrol edilir (üst çubuk sol düğme layout’ta) */
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
  hideMobileTrigger?: boolean;
}

type NavLink = { to: string; icon: React.ComponentType<{ size?: number; className?: string }>; label: string };

const linkBtn = (darkMode: boolean) =>
  darkMode
    ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 border-gray-700'
    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200';

export const mobileNavBtnClass = (darkMode: boolean) =>
  `shrink-0 px-2.5 py-2 rounded-lg border min-h-[44px] min-w-[44px] flex items-center justify-center ${linkBtn(darkMode)}`;

const PublicNav: React.FC<Props> = ({
  darkMode,
  className = '',
  mobileOpen: controlledOpen,
  onMobileOpenChange,
  hideMobileTrigger = false,
}) => {
  const { lang, setLang, t } = useLanguage();
  const [internalOpen, setInternalOpen] = useState(false);
  const mobileOpen = controlledOpen ?? internalOpen;
  const setMobileOpen = onMobileOpenChange ?? setInternalOpen;
  const btn = linkBtn(darkMode);
  const mobileBtn = mobileNavBtnClass(darkMode);

  const navLinkClass = `shrink-0 px-2 py-1.5 rounded-lg border text-sm flex items-center gap-1 whitespace-nowrap min-h-[40px] ${btn}`;
  const mobileLinkClass = `flex items-center gap-3 px-4 py-3 rounded-xl border text-base min-h-[48px] ${
    darkMode
      ? 'bg-gray-800/80 border-gray-700 text-gray-100 hover:bg-gray-700'
      : 'bg-white border-gray-200 text-gray-800 hover:bg-indigo-50'
  }`;

  const primaryLinks: NavLink[] = [
    { to: '/', icon: Home, label: t('home') },
    { to: '/category', icon: BarChart3, label: t('categories') },
    { to: '/software', icon: Code2, label: t('navSoftware') },
    { to: '/inactive', icon: Skull, label: t('navInactive') },
    { to: '/compare', icon: GitCompare, label: t('compare') },
    { to: '/watchlist', icon: Bookmark, label: t('navWatchlist') },
    { to: '/rising', icon: TrendingUp, label: t('rising') },
    { to: '/new-forums', icon: Sparkles, label: t('navCommunity') },
    { to: '/sponsor', icon: Sparkles, label: t('navSponsor') },
  ];

  const exploreItems: NavDropdownItem[] = [
    { type: 'link', to: '/leaderboard', icon: Trophy, label: t('leaderboard') },
    { type: 'link', to: '/index', icon: LineChart, label: t('tfeIndex') },
    { type: 'link', to: '/summary/weekly', icon: Calendar, label: t('weeklySummary') },
    { type: 'link', to: '/summary/monthly', icon: Calendar, label: t('monthlySummary') },
    { type: 'link', to: '/hall-of-fame', icon: Award, label: t('hallOfFame') },
    { type: 'separator' },
    { type: 'link', to: '/search', icon: Search, label: t('advancedSearch') },
    { type: 'link', to: '/compare-categories', icon: Tags, label: t('compareCategories') },
    { type: 'link', to: '/api-docs', icon: BookOpen, label: t('apiDocs') },
    { type: 'link', to: '/embed-docs', icon: Code2, label: t('embed') },
  ];

  const exploreLinks = exploreItems.filter((i): i is Extract<NavDropdownItem, { type: 'link' }> => i.type === 'link');

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const mobileMenu = mobileOpen
    ? createPortal(
        <div className="fixed inset-0 z-[100] md:hidden" role="dialog" aria-modal="true" aria-label="Mobil menü">
          <button
            type="button"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            aria-label="Menüyü kapat"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className={`absolute inset-x-0 top-0 max-h-[min(100dvh,100%)] overflow-y-auto shadow-2xl ${
              darkMode ? 'bg-gray-900 border-b border-gray-700' : 'bg-white border-b border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between px-3 py-3 border-b border-gray-200/20">
              <span className={`font-semibold ${darkMode ? 'text-white' : 'text-indigo-900'}`}>Menü</span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className={`p-2 rounded-lg border min-h-[44px] min-w-[44px] flex items-center justify-center ${btn}`}
                aria-label="Menüyü kapat"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="p-3 pb-6 grid grid-cols-2 gap-2" aria-label="Mobil site menüsü">
              {primaryLinks.map(({ to, icon: Icon, label }) => (
                <Link key={to} to={to} className={mobileLinkClass} onClick={() => setMobileOpen(false)}>
                  <Icon size={20} className="shrink-0 opacity-80" />
                  <span className="font-medium">{label}</span>
                </Link>
              ))}
            </nav>
            <div className={`px-3 pb-2 text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {t('explore')}
            </div>
            <nav className="px-3 pb-4 grid grid-cols-1 gap-2">
              {exploreLinks.map(({ to, icon: Icon, label }) => (
                <Link key={to} to={to} className={mobileLinkClass} onClick={() => setMobileOpen(false)}>
                  <Icon size={20} className="shrink-0 opacity-80" />
                  <span className="font-medium">{label}</span>
                </Link>
              ))}
            </nav>
            <div className="px-3 pb-6">
              <button
                type="button"
                onClick={() => {
                  setLang(lang === 'tr' ? 'en' : 'tr');
                  setMobileOpen(false);
                }}
                className={`w-full ${mobileLinkClass} justify-center`}
              >
                <Globe size={20} />
                <span>{lang === 'tr' ? 'English' : 'Türkçe'}</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <nav
        className={`flex flex-1 min-w-0 w-full items-center ${className ?? ''}`}
        aria-label="Site menüsü"
      >
        {!hideMobileTrigger && (
          <button
            type="button"
            className={`md:hidden ${mobileBtn}`}
            aria-expanded={mobileOpen}
            aria-label="Menüyü aç"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={22} />
          </button>
        )}

        <div className="hidden md:flex w-full flex-1 items-center justify-center gap-1 flex-wrap min-w-0">
          {primaryLinks.map(({ to, icon: Icon, label }) => (
            <Link key={to} to={to} className={navLinkClass} title={label}>
              <Icon size={16} className="shrink-0" />
              <span>{label}</span>
            </Link>
          ))}
          <NavDropdown label={t('explore')} items={exploreItems} darkMode={darkMode} btnClass={btn} icon={Compass} />
          <button
            type="button"
            onClick={() => setLang(lang === 'tr' ? 'en' : 'tr')}
            title={lang === 'tr' ? 'English' : 'Türkçe'}
            className={`shrink-0 px-2 py-1.5 rounded-lg border text-sm items-center gap-1 min-h-[40px] flex ${btn}`}
            aria-label="Dil değiştir"
          >
            <Globe size={16} />
            <span className="font-medium">{lang === 'tr' ? 'EN' : 'TR'}</span>
          </button>
        </div>
      </nav>
      {mobileMenu}
    </>
  );
};

export default PublicNav;
